import React, { useRef, useState, useCallback } from 'react';
import { Table2, ExternalLink, Search } from 'lucide-react';
import toast from 'react-hot-toast';

/** ID extrait de l’URL Google Sheets ; surcharge possible avec REACT_APP_GOOGLE_SHEET_ID */
const DEFAULT_SHEET_ID = '1MaX-X94KzGp_nds2n_rKpbd3FDLQYKk2VR4pOUyVJlo';

export default function GoogleSheetPage() {
  const sheetId = process.env.REACT_APP_GOOGLE_SHEET_ID || DEFAULT_SHEET_ID;
  const editUrl = `https://docs.google.com/spreadsheets/d/${sheetId}/edit`;
  /** Paramètres souvent utilisés pour l’intégration (Google peut quand même refuser l’iframe) */
  const embedUrl = `${editUrl}?rm=minimal&widget=true&headers=false`;

  const iframeRef = useRef(null);
  const [searchText, setSearchText] = useState('');

  const runSearch = useCallback(async () => {
    const q = searchText.trim();
    if (!q) {
      toast.error('Saisissez un texte à rechercher');
      return;
    }
    try {
      await navigator.clipboard.writeText(q);
    } catch {
      /* ignore — user can still type manually */
    }
    try {
      iframeRef.current?.focus();
    } catch {
      /* ignore */
    }
    toast.success(
      () => (
        <div>
          <p className="font-medium">Texte copié dans le presse-papiers.</p>
          <p className="text-sm mt-1 opacity-90">
            Cliquez dans la feuille ci-dessous, puis utilisez{' '}
            <kbd className="px-1 py-0.5 rounded bg-white/20 font-mono text-xs">Ctrl+F</kbd> (ou{' '}
            <kbd className="px-1 py-0.5 rounded bg-white/20 font-mono text-xs">Cmd+F</kbd> sur Mac) et collez (
            <kbd className="px-1 py-0.5 rounded bg-white/20 font-mono text-xs">Ctrl+V</kbd>).
          </p>
        </div>
      ),
      { duration: 8000 }
    );
  }, [searchText]);

  const openSearchInNewTab = useCallback(() => {
    const q = searchText.trim();
    window.open(editUrl, '_blank', 'noopener,noreferrer');
    if (q) {
      navigator.clipboard.writeText(q).catch(() => {});
      toast.success(
        'Feuille ouverte dans un nouvel onglet. Texte copié : utilisez Ctrl+F puis collez pour rechercher.',
        { duration: 6000 }
      );
    }
  }, [editUrl, searchText]);

  return (
    <div className="flex flex-col gap-4 max-w-[100vw]">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 flex items-center gap-2">
            <Table2 className="h-8 w-8 text-emerald-600" />
            Feuille Google
          </h1>
          <p className="text-neutral-600 mt-1 text-sm max-w-2xl">
            Consultation et modification dans Google Sheets. Si le tableau ne s’affiche pas ci-dessous (politique
            Google), utilisez le bouton pour l’ouvrir dans un nouvel onglet — l’édition y est toujours possible.
          </p>
        </div>
        <a
          href={editUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 text-white px-4 py-2.5 text-sm font-semibold hover:bg-emerald-700 shadow-sm shrink-0"
        >
          <ExternalLink className="h-4 w-4" />
          Ouvrir dans Google Sheets
        </a>
      </div>

      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-end gap-3 rounded-xl border border-neutral-200 bg-white px-4 py-3 shadow-sm">
        <div className="flex-1 min-w-[200px]">
          <label htmlFor="sheet-search" className="block text-xs font-medium text-neutral-600 mb-1">
            Rechercher dans la feuille
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400 pointer-events-none" />
              <input
                id="sheet-search"
                type="search"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    runSearch();
                  }
                }}
                placeholder="Mot, nombre, référence…"
                className="w-full rounded-lg border border-neutral-200 pl-9 pr-3 py-2 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none"
                autoComplete="off"
              />
            </div>
            <button
              type="button"
              onClick={runSearch}
              className="inline-flex items-center gap-2 rounded-lg bg-primary-600 text-white px-4 py-2 text-sm font-semibold hover:bg-primary-700 shrink-0"
            >
              <Search className="h-4 w-4" />
              Rechercher
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={openSearchInNewTab}
          className="text-sm text-primary-600 hover:text-primary-800 font-medium self-end sm:self-auto"
        >
          Rechercher dans un nouvel onglet
        </button>
      </div>
      <p className="text-xs text-neutral-500 -mt-2">
        Google ne permet pas d’ouvrir la fenêtre « Rechercher » depuis l’extérieur de la feuille. Le texte est copié
        pour un collage rapide après Ctrl+F (Cmd+F) dans la feuille.
      </p>

      <div className="rounded-xl border border-neutral-200 bg-neutral-50 overflow-hidden shadow-sm">
        <iframe
          ref={iframeRef}
          title="Feuille Google partagée"
          src={embedUrl}
          className="w-full border-0 bg-white"
          style={{ minHeight: 'min(75vh, 900px)', height: '75vh' }}
          allow="clipboard-read; clipboard-write; fullscreen"
        />
      </div>

      <p className="text-xs text-neutral-500">
        Partage : dans Google Sheets, « Partager » → ajoutez les comptes Google des collaborateurs, ou « Toute
        personne disposant du lien » avec le rôle Lecteur ou Éditeur selon vos besoins.
      </p>
    </div>
  );
}
